import { sql } from "@codemirror/lang-sql";
import { MergeView } from "@codemirror/merge";
import { EditorState } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import { useEffect, useRef } from "react";

interface SqlMergeViewProps {
  previous: string;
  current: string;
}

const strongDiffTheme = EditorView.theme({
  // Whole-line backgrounds for changed/inserted/deleted lines.
  ".cm-changedLine": {
    backgroundColor: "rgba(248, 113, 113, 0.22)",
  },
  ".cm-insertedLine": {
    backgroundColor: "rgba(74, 222, 128, 0.22)",
  },
  ".cm-deletedLine, .cm-deletedChunk": {
    backgroundColor: "rgba(248, 113, 113, 0.22)",
  },
  // Inline highlights — much stronger so eye catches the token-level diff.
  ".cm-changedText": {
    backgroundColor: "rgba(248, 113, 113, 0.55)",
    outline: "1px solid rgba(220, 38, 38, 0.7)",
  },
  ".cm-insertedLine .cm-changedText, .cm-insertedText": {
    backgroundColor: "rgba(74, 222, 128, 0.55)",
    outline: "1px solid rgba(22, 163, 74, 0.7)",
  },
  // Gutter markers so the diff is visible even on lines wrapped beyond view.
  ".cm-changedLineGutter, .cm-insertedLineGutter, .cm-deletedLineGutter": {
    backgroundColor: "rgba(248, 113, 113, 0.5)",
  },
  ".cm-insertedLineGutter": {
    backgroundColor: "rgba(74, 222, 128, 0.5)",
  },
});

/**
 * Side-by-side SQL diff viewer powered by CodeMirror's merge view.
 *
 * Both panes are read-only. Container manages its own height —
 * parent (Sheet) provides the available space.
 */
export function SqlMergeView({ previous, current }: SqlMergeViewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<MergeView | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const view = new MergeView({
      parent: containerRef.current,
      a: {
        doc: previous,
        extensions: [EditorState.readOnly.of(true), sql(), strongDiffTheme],
      },
      b: {
        doc: current,
        extensions: [EditorState.readOnly.of(true), sql(), strongDiffTheme],
      },
    });
    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, [previous, current]);

  return (
    <div ref={containerRef} className="h-full overflow-auto rounded border border-border text-xs" />
  );
}
